import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface MoreAction {
  key: string;
  label: string;
  icon: string;
  onClick: () => void;
  /** Inicia um grupo novo, separado por uma linha. */
  separatorBefore?: boolean;
  hint?: string;
}

const MENU_WIDTH = 256;

/**
 * Menu "Mais ações" da FCA: as acoes menos frequentes, fora da barra principal.
 * Desenhado no body (portal): o cartao do cabecalho corta o que transborda.
 */
export default function TreeMoreActions({ actions }: { actions: MoreAction[] }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const wrapper = useRef<HTMLDivElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!open || !wrapper.current) return;
    const place = () => {
      const r = wrapper.current!.getBoundingClientRect();
      const left = Math.min(Math.max(8, r.left), window.innerWidth - MENU_WIDTH - 8);
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const height = menu.current?.offsetHeight ?? 360;
      // Abre para cima so quando nao cabe embaixo e ha mais espaco em cima.
      if (height > below && above > below) {
        setPos({ top: Math.max(8, r.top - 4 - Math.min(height, above)), left, maxHeight: above });
      } else {
        setPos({ top: r.bottom + 4, left, maxHeight: below });
      }
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapper.current?.contains(t) && !menu.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="btn-outline text-xs w-full justify-center"
      >
        <i className="ri-more-2-fill" aria-hidden="true"></i>
        Mais ações
      </button>
      {open && createPortal(
        <div
          ref={menu}
          role="menu"
          style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? 0, width: MENU_WIDTH, maxHeight: pos?.maxHeight, visibility: pos ? "visible" : "hidden" }}
          className="bg-white border border-background-200 rounded-card shadow-lg z-[60] py-1 overflow-y-auto"
        >
          {actions.map((a) => (
            <div key={a.key}>
              {a.separatorBefore && <div className="my-1 border-t border-background-200" role="separator" />}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 flex items-start gap-2.5"
              >
                <i className={`${a.icon} text-base text-foreground-500 leading-5`} aria-hidden="true"></i>
                <span>
                  <span className="block">{a.label}</span>
                  {a.hint && <span className="block text-[11px] text-foreground-500 leading-snug">{a.hint}</span>}
                </span>
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
